import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid2';
import InputAdornment from '@mui/material/InputAdornment';
import { Trans } from '@lingui/react';
import Slider from '@mui/material/Slider';
import TextField from '@mui/material/TextField';
import { ClassNameMap } from '@mui/styles';
import { ProjectNumberField } from 'components/Form';
import { FormikHelpers } from 'formik';
import { BasicDisplayUnits, unitLabels } from 'hooks/useDisplayUnits/useDisplayUnits';
import { Form } from 'hooks/useForm';
import { BackfillRecipeForm, CustomUnitFields, UnitSystem } from 'providers/api';
import useProjectNumberFormatter from 'modules/project/providers/project/useProjectNumberFormatter';
import { round } from 'utils';
import { DictionaryRange } from '../../../MineModel/types';
import UnitSystemNumberField from './UnitSystemNumberField';

interface HydraulicAdjustedInputsProps {
  values: BackfillRecipeForm;
  limits: DictionaryRange;
  setFieldValue: FormikHelpers<BackfillRecipeForm>['setFieldValue'];
  setFieldTouched: FormikHelpers<BackfillRecipeForm>['setFieldTouched'];
  classes: ClassNameMap;
  unitSystemPreference: UnitSystem;
  customUnitFields: CustomUnitFields[];
  hasError: Form['helpers']['hasError'];
  getErrorHelpText: Form['helpers']['getErrorHelpText'];
  handleBlur: (e: any) => void | void;
}

const decimalToPercentage = (value: number) => (value + Number.EPSILON) * 100;

const HydraulicAdjustedInputs = ({
  values,
  limits,
  setFieldValue,
  setFieldTouched,
  classes,
  unitSystemPreference,
  customUnitFields,
  hasError,
  getErrorHelpText,
  handleBlur,
}: HydraulicAdjustedInputsProps) => {
  const formatNumber = useProjectNumberFormatter();

  return (
    <Grid container spacing={1}>
      <Grid size={12}>
        <Divider className={classes.fullWidthDivider} />
      </Grid>
      {/* Concentration: Optimal vs. Adjusted */}
      <Grid size={6}>
        <FormHelperText id="optimal-concentration-helper-text" component="label"><Trans id="Optimal Concentration">Optimal Concentration</Trans></FormHelperText>
        <TextField
          size="small"
          type="text"
          disabled
          fullWidth
          id="optimal-concentration-input"
          name="optimal-concentration-input"
          aria-labelledby="optimal-concentration-helper-text"
          value={formatNumber(values.hydraulicOptimisedSpecification?.massConcentration ?? 0, 2)}
          slotProps={{
            input: {
              endAdornment: <InputAdornment position="end">%m</InputAdornment>,
            },
          }}
        />
      </Grid>

      <Grid size={6}>
        <FormHelperText id="adjusted-concentration-helper-text" component="label"><Trans id="Adjusted Concentration">Adjusted Concentration</Trans></FormHelperText>
        <ProjectNumberField
          size="small"
          fullWidth
          id="hydraulicSelectedSpecification.massConcentration"
          name="hydraulicSelectedSpecification.massConcentration"
          decimalPlaces={2}
          value={values.hydraulicSelectedSpecification?.massConcentration ?? 0}
          onChange={(parsed) => setFieldValue(
            'hydraulicSelectedSpecification.massConcentration',
            parsed,
          )}
          error={hasError('hydraulicSelectedSpecification.massConcentration')}
          helperText={getErrorHelpText('hydraulicSelectedSpecification.massConcentration')}
          aria-labelledby="adjusted-concentration-helper-text"
          onBlur={() => {
            setFieldValue('hydraulicSelectedSpecification.massConcentration', Number(values.hydraulicSelectedSpecification?.massConcentration?.toFixed(2)));
            setFieldTouched('hydraulicSelectedSpecification.massConcentration', true, false);
          }}
          slotProps={{
            input: {
              endAdornment: <InputAdornment position="end">%m</InputAdornment>,
            },
          }}
        />
      </Grid>

      <Grid size={6} />

      <Grid size={6}>
        <Box sx={{
          mx: 1,
        }}
        >
          <Slider
            color="secondary"
            value={values.hydraulicSelectedSpecification?.massConcentration}
            min={decimalToPercentage(limits.concentration.min)}
            step={round(limits.concentration.interval * 100)}
            max={decimalToPercentage(limits.concentration.max)}
            onChange={(e, n) => setFieldValue('hydraulicSelectedSpecification.massConcentration', parseFloat((n as number).toFixed(2)))}
            aria-labelledby="adjusted-concentration-helper-text"
          />
        </Box>
      </Grid>

      {/* not editable but returned from optimiser */}
      <Grid size={6}>
        <FormHelperText id="settling-velocity-helper-text" component="label"><Trans id="Settling Velocity">Settling Velocity</Trans></FormHelperText>
        <UnitSystemNumberField
          unitSystem={unitSystemPreference}
          customUnitFields={customUnitFields}
          unitInfo={unitLabels[BasicDisplayUnits.Speed]}
          disabled
          size="small"
          decimalPlaces={2}
          id="settling-velocity"
          name="settling-velocity"
          aria-labelledby="settling-velocity-helper-text"
          value={round(values.hydraulicOptimisedSpecification?.settlingVelocity ?? 0)}
          error={hasError('hydraulicOptimisedSpecification.settlingVelocity')}
          helperText={getErrorHelpText('hydraulicOptimisedSpecification.settlingVelocity')}
          onBlur={handleBlur}
        />
      </Grid>

      <Grid size={6}>
        <FormHelperText id="minimum-velocity-helper-text" component="label"><Trans id="Minimum Velocity">Minimum Velocity</Trans></FormHelperText>
        <UnitSystemNumberField
          unitSystem={unitSystemPreference}
          customUnitFields={customUnitFields}
          unitInfo={unitLabels[BasicDisplayUnits.Speed]}
          disabled
          decimalPlaces={2}
          size="small"
          type="number"
          fullWidth
          id="mimimum-velocity-input"
          name="minimum-velocity-input"
          value={round(values.hydraulicOptimisedSpecification?.minVelocity ?? 0)}
          aria-labelledby="minimum-velocity-helper-text"
          error={hasError('hydraulicOptimisedSpecification.minVelocity')}
          helperText={getErrorHelpText('hydraulicOptimisedSpecification.minVelocity')}
          onBlur={handleBlur}
        />
      </Grid>
    </Grid>
  );
};

export default HydraulicAdjustedInputs;
